import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useLocation, useSearchParams } from 'react-router-dom'

import { CategoryGenreSelection } from '../components/MarketAnalyticsFilterBar'
import {
  CATALOGUE_METRIC_KEYS,
  CATALOGUE_WINDOWS,
  CatalogueGranularity,
  CatalogueListSort,
  CatalogueTreeSortDirection,
  CatalogueTreeSortKey,
  CatalogueViewMode,
  CatalogueWindow,
  DEFAULT_CATALOGUE_WINDOW,
  decodeExpandedNodeId,
  encodeExpandedNodeId,
} from '../components/helpers/catalogueUtils'

const GRANULARITIES: CatalogueGranularity[] = ['eventCategory', 'eventType', 'blueprint']
const VIEW_MODES: CatalogueViewMode[] = ['list', 'chart']
// Note: a legacy 'liveAdoption' value (removed lens) in a bookmarked/shared URL
// is not in this list, so `parseEnum` gracefully falls it back to 'adoption'.
const LIST_SORTS: CatalogueListSort[] = CATALOGUE_METRIC_KEYS
const TREE_SORT_KEYS: CatalogueTreeSortKey[] = ['metric', 'change']
const TREE_SORT_DIRECTIONS: CatalogueTreeSortDirection[] = ['asc', 'desc']
// Defaults for the ranked-list tree sort; the `tsort`/`tdir` params stay off
// the URL while the sort matches them (see setTreeSort).
const DEFAULT_TREE_SORT_KEY: CatalogueTreeSortKey = 'metric'
const DEFAULT_TREE_SORT_DIR: CatalogueTreeSortDirection = 'desc'

export interface MarketCatalogueState {
  categoryGenre: CategoryGenreSelection
  eventCategoryIds: string[]
  eventTypeIds: string[]
  granularity: CatalogueGranularity
  viewMode: CatalogueViewMode
  listSort: CatalogueListSort
  window: CatalogueWindow
  // Expanded node ids of the ranked-list tree (CSV `exp` param), so the
  // expansion survives the detail-page round trip and shared links. Stale ids
  // (from an old granularity / filter set) are harmless — they simply don't
  // match any rendered node.
  expandedNodeIds: string[]
  // Ranked-list tree column sort (`tsort`/`tdir` params). Distinct from
  // `listSort`, which is the metric lens (adoption / density / concurrent
  // instances) shared by the list column and the chart series.
  treeSortKey: CatalogueTreeSortKey
  treeSortDir: CatalogueTreeSortDirection
}

// Parent → child lookups derived from the genre taxonomy, used to prune
// orphaned genre / subgenre selections when a parent category or genre is
// deselected (keeps the cascading filter rows and the URL in sync).
export interface TaxonomyRelations {
  genreToCategory: Record<string, string>
  subgenreToCategory: Record<string, string>
  subgenreToGenre: Record<string, string>
}

// Event category (tag group) id → the event type (tag) ids filed under it. Only
// pass it once the event tag groups have loaded and are non-empty: an
// `ecat`/`etype` it doesn't know is then dropped from the state and the URL.
export type EventTaxonomy = ReadonlyMap<string, readonly string[]>

export interface MarketCatalogueStateApi extends MarketCatalogueState {
  // Apply a whole taxonomy level at once. The segment picker stages its
  // checkboxes and commits them on Done, so a multi-pick arrives as one set.
  setCategories: (ids: string[]) => void
  setGenres: (ids: string[]) => void
  setSubgenres: (ids: string[]) => void
  toggleCategory: (id: string) => void
  toggleGenre: (id: string) => void
  toggleSubgenre: (id: string) => void
  clearCategories: () => void
  clearGenres: () => void
  clearSubgenres: () => void
  clearTaxonomy: () => void
  toggleEventCategory: (id: string) => void
  toggleEventType: (id: string) => void
  clearEventCategories: () => void
  clearEventTypes: () => void
  clearEvents: () => void
  setGranularity: (next: CatalogueGranularity) => void
  setViewMode: (next: CatalogueViewMode) => void
  setListSort: (next: CatalogueListSort) => void
  setWindow: (next: CatalogueWindow) => void
  toggleExpandedNode: (id: string) => void
  clearExpandedNodes: () => void
  setTreeSort: (key: CatalogueTreeSortKey, dir: CatalogueTreeSortDirection) => void
}

function parseList(value: string | null): string[] {
  if (!value) return []
  return value
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
}

// `ids` plus `parentId` when it is known and not already present. Without the
// taxonomy relations (still loading) there is nothing to add, and the selection
// is left as-is rather than guessed.
function withParent(ids: string[], parentId: string | undefined): string[] {
  if (!parentId || ids.includes(parentId)) return ids
  return [...ids, parentId]
}

function parseEnum<T extends string>(value: string | null, allowed: T[], fallback: T): T {
  return value && (allowed as string[]).includes(value) ? (value as T) : fallback
}

// CSV param value, or undefined so an empty level drops out of the URL.
function toCsv(ids: readonly string[]): string | undefined {
  return ids.length ? ids.join(',') : undefined
}

function toggled(ids: readonly string[], id: string): string[] {
  return ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]
}

/**
 * Router state to keep across a filter write. The detail pages' back crumb reads
 * `catalogueBackSearch` from it, so dropping the state would lose the catalogue view
 * to return to. The momentum snapshot (`deltaGames` / `cohortSize` /
 * `deltaWindowDays`) is left behind: it was taken for the scope the user just
 * changed, so the page should fetch its own.
 */
export function carriedNavState(state: unknown): unknown {
  if (!state || typeof state !== 'object') return state
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { deltaGames, cohortSize, deltaWindowDays, ...rest } = state as Record<string, unknown>
  return rest
}

/**
 * Drops event category / type ids the event taxonomy doesn't know, so a stale or
 * hand-edited `ecat`/`etype` reads as "All" instead of filtering the catalogue
 * down to nothing. Event types are a level below categories: with categories
 * selected, only their types are kept. The input arrays are returned as-is when
 * nothing was dropped (or there's no taxonomy yet), keeping identities stable.
 */
export function resolveEventSelection(
  eventCategoryIds: string[],
  eventTypeIds: string[],
  eventTaxonomy?: EventTaxonomy
): { eventCategoryIds: string[]; eventTypeIds: string[] } {
  if (!eventTaxonomy?.size) return { eventCategoryIds, eventTypeIds }
  const knownCategoryIds = eventCategoryIds.filter((id) => eventTaxonomy.has(id))
  const typeScope = knownCategoryIds.length ? knownCategoryIds : Array.from(eventTaxonomy.keys())
  const allowedTypeIds = new Set(typeScope.flatMap((id) => eventTaxonomy.get(id) ?? []))
  const knownTypeIds = eventTypeIds.filter((id) => allowedTypeIds.has(id))
  return {
    eventCategoryIds: knownCategoryIds.length === eventCategoryIds.length ? eventCategoryIds : knownCategoryIds,
    eventTypeIds: knownTypeIds.length === eventTypeIds.length ? eventTypeIds : knownTypeIds,
  }
}

/**
 * Single source of truth for the Market Catalogue browser, backed by the URL
 * query string so a view is fully shareable / deep-linkable. Extends the
 * pre-existing `category`/`genre`/`subgenre` taxonomy params.
 */
export function useMarketCatalogueState(relations?: TaxonomyRelations, eventTaxonomy?: EventTaxonomy): MarketCatalogueStateApi {
  const [searchParams, setSearchParams] = useSearchParams()
  // Read through a ref so `patchParams` (and every setter built on it) keeps its
  // identity across navigations.
  const location = useLocation()
  const locationStateRef = useRef<unknown>(location.state)
  locationStateRef.current = location.state

  // `searchParams` gets a NEW identity on every URL write, so anything derived
  // from it directly would too — even when the underlying param didn't change.
  // Each parsed list is therefore memoized on its raw param STRING (strings are
  // value-stable), so e.g. expanding a tree node (`exp`) no longer mints new
  // array identities for the filter lists and cascades a full tree rebuild.
  const categoryCsv = searchParams.get('category')
  const genreCsv = searchParams.get('genre')
  const subgenreCsv = searchParams.get('subgenre')
  const ecatCsv = searchParams.get('ecat')
  const etypeCsv = searchParams.get('etype')
  const expandedCsv = searchParams.get('exp')

  // Taxonomy rows are independent multi-selects backed by CSV params. A legacy
  // single-value deep link (e.g. `?subgenre=abc`) parses cleanly to a
  // one-element array.
  const conventionalCategoryIds = useMemo(() => parseList(categoryCsv), [categoryCsv])
  const conventionalGenreIds = useMemo(() => parseList(genreCsv), [genreCsv])
  const conventionalSubgenreIds = useMemo(() => parseList(subgenreCsv), [subgenreCsv])
  const urlEventCategoryIds = useMemo(() => parseList(ecatCsv), [ecatCsv])
  const urlEventTypeIds = useMemo(() => parseList(etypeCsv), [etypeCsv])
  const { eventCategoryIds, eventTypeIds } = useMemo(
    () => resolveEventSelection(urlEventCategoryIds, urlEventTypeIds, eventTaxonomy),
    [urlEventCategoryIds, urlEventTypeIds, eventTaxonomy]
  )
  // `exp` tokens are base64url-encoded node ids (see encodeExpandedNodeId);
  // undecodable tokens (old-format hex links, mangled values) drop out here.
  const expandedNodeIds = useMemo(() => parseList(expandedCsv).flatMap((token) => decodeExpandedNodeId(token) ?? []), [expandedCsv])

  const categoryGenre = useMemo<CategoryGenreSelection>(
    () => ({ conventionalCategoryIds, conventionalGenreIds, conventionalSubgenreIds }),
    [conventionalCategoryIds, conventionalGenreIds, conventionalSubgenreIds]
  )

  // Enum / scalar params are value-stable primitives, no memo needed.
  const granularity = parseEnum(searchParams.get('gran'), GRANULARITIES, 'eventCategory')
  const viewMode = parseEnum(searchParams.get('view'), VIEW_MODES, 'list')
  const listSort = parseEnum(searchParams.get('sort'), LIST_SORTS, 'adoption')
  const catalogueWindow = parseEnum(searchParams.get('win'), CATALOGUE_WINDOWS, DEFAULT_CATALOGUE_WINDOW)
  const treeSortKey = parseEnum(searchParams.get('tsort'), TREE_SORT_KEYS, DEFAULT_TREE_SORT_KEY)
  const treeSortDir = parseEnum(searchParams.get('tdir'), TREE_SORT_DIRECTIONS, DEFAULT_TREE_SORT_DIR)

  // Merge a partial set of param changes onto the current query string. Any key
  // set to undefined/'' is removed so the URL stays clean. Router state rides
  // along (see carriedNavState).
  const patchParams = useCallback(
    (patch: Record<string, string | undefined>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          for (const [key, value] of Object.entries(patch)) {
            if (value) next.set(key, value)
            else next.delete(key)
          }
          return next
        },
        { replace: true, state: carriedNavState(locationStateRef.current) }
      )
    },
    [setSearchParams]
  )

  // Write the validated event selection back (as a replace) so a shared or
  // bookmarked link doesn't keep carrying ids the catalogue ignores.
  useEffect(() => {
    if (eventCategoryIds !== urlEventCategoryIds || eventTypeIds !== urlEventTypeIds) {
      patchParams({ ecat: toCsv(eventCategoryIds), etype: toCsv(eventTypeIds) })
    }
  }, [eventCategoryIds, eventTypeIds, urlEventCategoryIds, urlEventTypeIds, patchParams])

  const toggleInList = useCallback((key: 'ecat' | 'etype', current: string[], id: string) => patchParams({ [key]: toCsv(toggled(current, id)) }), [patchParams])

  // Cascading level setters, each a single URL write. Deselecting a category (or
  // genre) prunes any genre / subgenre selection that no longer has a selected
  // parent. Selecting a genre (or subgenre) pulls its parents in, so the rows
  // always describe a path down the tree — the segment filter offers children of
  // unselected parents (e.g. any subgenre while no genre is picked), and a child
  // without its ancestors selected would be unreachable from the chips.
  //
  // These take a whole level rather than one id because the picker stages its
  // checkboxes and commits on Done: a run of single toggles would each re-filter
  // the page mid-selection, and consecutive calls would read each other's stale
  // selection anyway.
  const setCategories = useCallback(
    (ids: string[]) => {
      const nextSet = new Set(ids)
      const genres = relations ? conventionalGenreIds.filter((gid) => nextSet.has(relations.genreToCategory[gid])) : conventionalGenreIds
      const genreSet = new Set(genres)
      const subgenres = relations
        ? conventionalSubgenreIds.filter((sid) => nextSet.has(relations.subgenreToCategory[sid]) && genreSet.has(relations.subgenreToGenre[sid]))
        : conventionalSubgenreIds
      patchParams({ category: toCsv(ids), genre: toCsv(genres), subgenre: toCsv(subgenres) })
    },
    [patchParams, relations, conventionalGenreIds, conventionalSubgenreIds]
  )
  const setGenres = useCallback(
    (ids: string[]) => {
      const nextSet = new Set(ids)
      const subgenres = relations ? conventionalSubgenreIds.filter((sid) => nextSet.has(relations.subgenreToGenre[sid])) : conventionalSubgenreIds
      // Only the kept ids pull parents in, so removing the last genre of a
      // category leaves that category selected rather than resurrecting it.
      const categories = relations ? ids.reduce((acc, id) => withParent(acc, relations.genreToCategory[id]), conventionalCategoryIds) : conventionalCategoryIds
      patchParams({ category: toCsv(categories), genre: toCsv(ids), subgenre: toCsv(subgenres) })
    },
    [patchParams, relations, conventionalCategoryIds, conventionalSubgenreIds]
  )
  const setSubgenres = useCallback(
    (ids: string[]) => {
      const genres = relations ? ids.reduce((acc, id) => withParent(acc, relations.subgenreToGenre[id]), conventionalGenreIds) : conventionalGenreIds
      const categories = relations
        ? ids.reduce((acc, id) => withParent(acc, relations.subgenreToCategory[id]), conventionalCategoryIds)
        : conventionalCategoryIds
      patchParams({ category: toCsv(categories), genre: toCsv(genres), subgenre: toCsv(ids) })
    },
    [patchParams, relations, conventionalCategoryIds, conventionalGenreIds]
  )

  const toggleCategory = useCallback((id: string) => setCategories(toggled(conventionalCategoryIds, id)), [setCategories, conventionalCategoryIds])
  const toggleGenre = useCallback((id: string) => setGenres(toggled(conventionalGenreIds, id)), [setGenres, conventionalGenreIds])
  const toggleSubgenre = useCallback((id: string) => setSubgenres(toggled(conventionalSubgenreIds, id)), [setSubgenres, conventionalSubgenreIds])

  // Clearing a level widens the scope there and collapses every deeper row.
  const clearCategories = useCallback(() => patchParams({ category: undefined, genre: undefined, subgenre: undefined }), [patchParams])
  const clearGenres = useCallback(() => patchParams({ genre: undefined, subgenre: undefined }), [patchParams])
  const clearSubgenres = useCallback(() => patchParams({ subgenre: undefined }), [patchParams])
  // Clearing the top level already clears all three taxonomy dimensions in one write.
  const clearTaxonomy = clearCategories

  // Event types the new category scope no longer covers are pruned by
  // resolveEventSelection and written back by the effect above; the ones still
  // under a selected category stay picked.
  const toggleEventCategory = useCallback((id: string) => toggleInList('ecat', eventCategoryIds, id), [toggleInList, eventCategoryIds])

  const toggleEventType = useCallback(
    (id: string) => {
      toggleInList('etype', eventTypeIds, id)
    },
    [toggleInList, eventTypeIds]
  )

  const clearEventCategories = useCallback(() => patchParams({ ecat: undefined, etype: undefined }), [patchParams])
  const clearEventTypes = useCallback(() => patchParams({ etype: undefined }), [patchParams])
  // Clearing the categories already clears both event dimensions in one write.
  const clearEvents = clearEventCategories

  // Changing granularity rebuilds the tree with differently-namespaced node
  // ids (event-group ids vs `${groupId}:${tagId}` composites vs flat leaves),
  // so the expansion set is cleared in the same URL write — carrying it over
  // would only keep meaningless ids in the URL.
  const setGranularity = useCallback((next: CatalogueGranularity) => patchParams({ gran: next, exp: undefined }), [patchParams])
  const setViewMode = useCallback((next: CatalogueViewMode) => patchParams({ view: next }), [patchParams])
  const setListSort = useCallback((next: CatalogueListSort) => patchParams({ sort: next }), [patchParams])
  const setWindow = useCallback((next: CatalogueWindow) => patchParams({ win: next }), [patchParams])
  // Like toggleInList, but the URL value carries base64url-encoded tokens
  // while callers keep working with raw node ids.
  const toggleExpandedNode = useCallback(
    (id: string) => {
      const next = expandedNodeIds.includes(id) ? expandedNodeIds.filter((x) => x !== id) : [...expandedNodeIds, id]
      patchParams({ exp: next.length ? next.map(encodeExpandedNodeId).join(',') : undefined })
    },
    [patchParams, expandedNodeIds]
  )
  const clearExpandedNodes = useCallback(() => patchParams({ exp: undefined }), [patchParams])
  // Both params are dropped from the URL when they match the defaults, so the
  // default state (metric column, descending) leaves the query string clean.
  const setTreeSort = useCallback(
    (key: CatalogueTreeSortKey, dir: CatalogueTreeSortDirection) =>
      patchParams({
        tsort: key === DEFAULT_TREE_SORT_KEY ? undefined : key,
        tdir: dir === DEFAULT_TREE_SORT_DIR ? undefined : dir,
      }),
    [patchParams]
  )

  // Memoized so `state` itself is a stable dependency — consumers can safely
  // depend on the whole object (or effects can list it) without re-running on
  // unrelated renders.
  return useMemo<MarketCatalogueStateApi>(
    () => ({
      categoryGenre,
      eventCategoryIds,
      eventTypeIds,
      granularity,
      viewMode,
      listSort,
      window: catalogueWindow,
      expandedNodeIds,
      treeSortKey,
      treeSortDir,
      setCategories,
      setGenres,
      setSubgenres,
      toggleCategory,
      toggleGenre,
      toggleSubgenre,
      clearCategories,
      clearGenres,
      clearSubgenres,
      clearTaxonomy,
      toggleEventCategory,
      toggleEventType,
      clearEventCategories,
      clearEventTypes,
      clearEvents,
      setGranularity,
      setViewMode,
      setListSort,
      setWindow,
      toggleExpandedNode,
      clearExpandedNodes,
      setTreeSort,
    }),
    [
      categoryGenre,
      eventCategoryIds,
      eventTypeIds,
      granularity,
      viewMode,
      listSort,
      catalogueWindow,
      expandedNodeIds,
      treeSortKey,
      treeSortDir,
      setCategories,
      setGenres,
      setSubgenres,
      toggleCategory,
      toggleGenre,
      toggleSubgenre,
      clearCategories,
      clearGenres,
      clearSubgenres,
      clearTaxonomy,
      toggleEventCategory,
      toggleEventType,
      clearEventCategories,
      clearEventTypes,
      clearEvents,
      setGranularity,
      setViewMode,
      setListSort,
      setWindow,
      toggleExpandedNode,
      clearExpandedNodes,
      setTreeSort,
    ]
  )
}
